import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { coursesService } from '../../services/api';
import type { AdminCourseChapter, AdminCourseLesson, Audio, Video } from '../../types';

function formatDurationSec(total: number | null | undefined): string {
  if (total == null || !Number.isFinite(total) || total < 0) return '—';
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function lessonDurationSeconds(l: AdminCourseLesson): number {
  const v = l.video?.duration ?? 0;
  const a = l.audio?.duration ?? 0;
  return Math.max(0, v + a);
}

function chapterFromApi(row: {
  id: string;
  courseId: string;
  title: string;
  description?: string | null;
  sortOrder: number;
}): AdminCourseChapter {
  return {
    id: row.id,
    courseId: row.courseId,
    title: row.title,
    description: row.description ?? null,
    sortOrder: row.sortOrder,
    lessons: [],
  };
}

type Props = {
  courseId: string;
  videos: Video[];
  audios: Audio[];
};

export default function CourseCurriculumTab({ courseId, videos, audios }: Props) {
  const [chapters, setChapters] = useState<AdminCourseChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterDesc, setNewChapterDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [focusLessonId, setFocusLessonId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');

  const chapterListRef = useRef<HTMLDivElement | null>(null);
  const chapterItemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const newChapterInputRef = useRef<HTMLInputElement | null>(null);

  const sortedVideos = useMemo(() => [...videos].sort((a, b) => a.order - b.order), [videos]);
  const sortedAudios = useMemo(
    () => [...audios].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [audios],
  );

  const activeChapter = useMemo(
    () => chapters.find((ch) => ch.id === activeChapterId) ?? null,
    [chapters, activeChapterId],
  );

  const load = useCallback(async (preserveActiveId?: string | null) => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await coursesService.getCurriculumAdmin(courseId);
      const sorted = [...data].sort((a, b) => a.sortOrder - b.sortOrder);
      setChapters(sorted);
      setActiveChapterId((prev) => {
        const candidate = preserveActiveId ?? prev;
        if (candidate && sorted.some((ch) => ch.id === candidate)) return candidate;
        return sorted[0]?.id ?? null;
      });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'خطا در بارگذاری سرفصل‌ها';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!statusMessage) return;
    const timer = window.setTimeout(() => setStatusMessage(''), 2600);
    return () => window.clearTimeout(timer);
  }, [statusMessage]);

  const stats = useMemo(() => {
    let lessonCount = 0;
    let sec = 0;
    chapters.forEach((ch) => {
      ch.lessons.forEach((l) => {
        lessonCount += 1;
        sec += lessonDurationSeconds(l);
      });
    });
    return {
      chapterCount: chapters.length,
      lessonCount,
      minutes: Math.round(sec / 60),
    };
  }, [chapters]);

  const scrollChapterIntoView = (chapterId: string) => {
    window.requestAnimationFrame(() => {
      chapterItemRefs.current[chapterId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handleCreateChapter = async () => {
    const title = newChapterTitle.trim();
    if (!title) return;
    setBusy(true);
    try {
      const created = await coursesService.createChapter(courseId, {
        title,
        description: newChapterDesc.trim() || undefined,
      });
      const nextChapter = chapterFromApi(created);
      setChapters((prev) => [...prev, nextChapter].sort((a, b) => a.sortOrder - b.sortOrder));
      setActiveChapterId(nextChapter.id);
      setNewChapterTitle('');
      setNewChapterDesc('');
      setStatusMessage('فصل جدید اضافه شد');
      scrollChapterIntoView(nextChapter.id);
      newChapterInputRef.current?.focus();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'خطا در ایجاد فصل';
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveChapter = async (ch: AdminCourseChapter, title: string, description: string) => {
    setBusy(true);
    try {
      const updated = await coursesService.updateChapter(courseId, ch.id, {
        title: title.trim(),
        description: description.trim() || undefined,
      });
      setChapters((prev) =>
        prev.map((item) =>
          item.id === ch.id
            ? {
                ...item,
                title: updated.title,
                description: updated.description ?? null,
                sortOrder: updated.sortOrder,
              }
            : item,
        ),
      );
      setStatusMessage('فصل ذخیره شد');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'خطا در ذخیره فصل';
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteChapter = async (chapterId: string) => {
    if (!window.confirm('این فصل و همه جلسه‌هایش حذف شوند؟')) return;
    setBusy(true);
    try {
      await coursesService.deleteChapter(courseId, chapterId);
      setChapters((prev) => {
        const next = prev.filter((ch) => ch.id !== chapterId);
        setActiveChapterId((current) => {
          if (current !== chapterId) return current;
          return next[0]?.id ?? null;
        });
        return next;
      });
      setStatusMessage('فصل حذف شد');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'خطا در حذف فصل';
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleAddLesson = async (chapterId: string) => {
    setBusy(true);
    try {
      const created = await coursesService.createLesson(courseId, chapterId, {
        title: 'جلسه جدید',
        caption: '',
        published: true,
      });
      setChapters((prev) =>
        prev.map((ch) =>
          ch.id === chapterId
            ? { ...ch, lessons: [...ch.lessons, created].sort((a, b) => a.sortOrder - b.sortOrder) }
            : ch,
        ),
      );
      setActiveChapterId(chapterId);
      setFocusLessonId(created.id);
      setStatusMessage('جلسه جدید اضافه شد');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'خطا در ایجاد جلسه';
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveLesson = async (
    lessonId: string,
    payload: {
      title: string;
      caption: string;
      published: boolean;
      videoId: string;
      audioId: string;
    },
  ) => {
    setBusy(true);
    try {
      const updated = await coursesService.updateLesson(courseId, lessonId, {
        title: payload.title.trim(),
        caption: payload.caption.trim() || undefined,
        published: payload.published,
        videoId: payload.videoId ? payload.videoId : null,
        audioId: payload.audioId ? payload.audioId : null,
      });
      setChapters((prev) =>
        prev.map((ch) => ({
          ...ch,
          lessons: ch.lessons.map((lesson) => (lesson.id === lessonId ? updated : lesson)),
        })),
      );
      setStatusMessage('جلسه ذخیره شد');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'خطا در ذخیره جلسه';
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!window.confirm('این جلسه حذف شود؟')) return;
    setBusy(true);
    try {
      await coursesService.deleteLesson(courseId, lessonId);
      setChapters((prev) =>
        prev.map((ch) => ({
          ...ch,
          lessons: ch.lessons.filter((lesson) => lesson.id !== lessonId),
        })),
      );
      setStatusMessage('جلسه حذف شد');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'خطا در حذف جلسه';
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12 text-sm text-slate-600">
        در حال بارگذاری سرفصل‌ها…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        {loadError}
        <button type="button" onClick={() => load()} className="mt-2 block text-blue-700 hover:underline">
          تلاش دوباره
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-right" dir="rtl">
      <div className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white to-slate-50/80 p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-[#4c1d95]">محتوای دوره (سرفصل و جلسه)</h3>
            <p className="mt-1 text-sm text-slate-600">
              از لیست کنار، فصل را انتخاب کنید؛ جلسه‌ها فقط برای همان فصل نمایش داده می‌شوند — بدون اسکرول طولانی.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {statusMessage ? (
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                {statusMessage}
              </span>
            ) : null}
            <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-800">
              {stats.chapterCount} فصل
            </span>
            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-800">
              {stats.lessonCount} جلسه
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              ~{stats.minutes} دقیقه
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:min-h-[520px] lg:flex-row lg:items-stretch">
        <aside className="flex shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:w-[17.5rem] xl:w-[19rem]">
          <div className="border-b border-slate-100 px-3 py-2.5">
            <p className="text-xs font-bold text-slate-800">فهرست فصل‌ها</p>
            <p className="mt-0.5 text-[11px] text-slate-500">برای ویرایش، یک فصل را انتخاب کنید</p>
          </div>

          <div ref={chapterListRef} className="max-h-[220px] space-y-1 overflow-y-auto p-2 lg:max-h-none lg:flex-1">
            {chapters.length === 0 ? (
              <p className="px-2 py-4 text-center text-xs text-slate-500">هنوز فصلی نیست — پایین یک فصل بسازید.</p>
            ) : (
              chapters.map((ch, index) => {
                const isActive = ch.id === activeChapterId;
                const lessonCount = ch.lessons.length;
                return (
                  <button
                    key={ch.id}
                    type="button"
                    ref={(el) => {
                      chapterItemRefs.current[ch.id] = el;
                    }}
                    onClick={() => setActiveChapterId(ch.id)}
                    className={`w-full rounded-xl border px-3 py-2.5 text-right transition ${
                      isActive
                        ? 'border-violet-300 bg-violet-50 shadow-sm'
                        : 'border-transparent bg-slate-50/80 hover:border-slate-200 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold ${
                          isActive ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-slate-900">{ch.title}</p>
                        <p className="mt-0.5 text-[11px] text-slate-500">{lessonCount} جلسه</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t border-violet-100 bg-violet-50/70 p-3">
            <p className="mb-2 text-xs font-bold text-violet-900">+ فصل جدید</p>
            <input
              ref={newChapterInputRef}
              type="text"
              placeholder="عنوان فصل"
              value={newChapterTitle}
              onChange={(e) => setNewChapterTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleCreateChapter();
                }
              }}
              className="w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm"
            />
            <textarea
              placeholder="توضیح کوتاه (اختیاری)"
              value={newChapterDesc}
              onChange={(e) => setNewChapterDesc(e.target.value)}
              rows={2}
              className="mt-2 w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm"
            />
            <button
              type="button"
              disabled={busy || !newChapterTitle.trim()}
              onClick={() => void handleCreateChapter()}
              className="mt-2 w-full rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
            >
              ایجاد فصل
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-white shadow-sm">
          {activeChapter ? (
            <ChapterEditorPanel
              chapter={activeChapter}
              chapterIndex={chapters.findIndex((ch) => ch.id === activeChapter.id) + 1}
              onSaveChapter={handleSaveChapter}
              onDeleteChapter={handleDeleteChapter}
              onAddLesson={handleAddLesson}
              onSaveLesson={handleSaveLesson}
              onDeleteLesson={handleDeleteLesson}
              videos={sortedVideos}
              audios={sortedAudios}
              busy={busy}
              focusLessonId={focusLessonId}
              onFocusLessonHandled={() => setFocusLessonId(null)}
            />
          ) : (
            <div className="flex min-h-[280px] flex-col items-center justify-center px-6 py-12 text-center">
              <p className="text-sm font-semibold text-slate-700">فصلی برای ویرایش انتخاب نشده</p>
              <p className="mt-1 max-w-sm text-xs text-slate-500">
                از ستون کنار یک فصل انتخاب کنید یا با فرم پایین همان ستون، اولین فصل را بسازید.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function ChapterEditorPanel({
  chapter,
  chapterIndex,
  onSaveChapter,
  onDeleteChapter,
  onAddLesson,
  onSaveLesson,
  onDeleteLesson,
  videos,
  audios,
  busy,
  focusLessonId,
  onFocusLessonHandled,
}: {
  chapter: AdminCourseChapter;
  chapterIndex: number;
  onSaveChapter: (ch: AdminCourseChapter, title: string, description: string) => void;
  onDeleteChapter: (id: string) => void;
  onAddLesson: (chapterId: string) => void;
  onSaveLesson: (
    lessonId: string,
    p: { title: string; caption: string; published: boolean; videoId: string; audioId: string },
  ) => void;
  onDeleteLesson: (lessonId: string) => void;
  videos: Video[];
  audios: Audio[];
  busy: boolean;
  focusLessonId: string | null;
  onFocusLessonHandled: () => void;
}) {
  const [title, setTitle] = useState(chapter.title);
  const [description, setDescription] = useState(chapter.description || '');
  const lessonsScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setTitle(chapter.title);
    setDescription(chapter.description || '');
  }, [chapter.id, chapter.title, chapter.description]);

  useEffect(() => {
    if (!focusLessonId) return;
    const el = document.getElementById(`lesson-editor-${focusLessonId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusLessonId, chapter.lessons.length]);

  const chSec = chapter.lessons.reduce((s, l) => s + lessonDurationSeconds(l), 0);

  return (
    <div className="flex h-full min-h-[420px] flex-col">
      <div className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 px-4 py-3 backdrop-blur-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold text-violet-700">فصل {chapterIndex}</p>
            <h4 className="text-base font-bold text-slate-900">{title || chapter.title}</h4>
            <p className="mt-0.5 text-[11px] text-slate-500">
              {chapter.lessons.length} جلسه · {formatDurationSec(chSec)} جمع مدت
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => onAddLesson(chapter.id)}
              className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
            >
              + جلسه جدید
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onSaveChapter(chapter, title, description)}
              className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
            >
              ذخیره فصل
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onDeleteChapter(chapter.id)}
              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              حذف فصل
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4 border-b border-slate-100 px-4 py-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">عنوان فصل</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">توضیح فصل</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div ref={lessonsScrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4 lg:max-h-[min(58vh,640px)]">
        {chapter.lessons.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
            <p className="text-sm font-semibold text-slate-700">هنوز جلسه‌ای در این فصل نیست</p>
            <button
              type="button"
              disabled={busy}
              onClick={() => onAddLesson(chapter.id)}
              className="mt-3 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
            >
              + اولین جلسه
            </button>
          </div>
        ) : (
          chapter.lessons.map((lesson, idx) => (
            <LessonEditor
              key={lesson.id}
              lesson={lesson}
              indexLabel={idx + 1}
              videos={videos}
              audios={audios}
              busy={busy}
              focusLessonId={focusLessonId}
              onFocusLessonHandled={onFocusLessonHandled}
              onSave={onSaveLesson}
              onDelete={onDeleteLesson}
            />
          ))
        )}
      </div>
    </div>
  );
}

function LessonEditor({
  lesson,
  indexLabel,
  videos,
  audios,
  busy,
  focusLessonId,
  onFocusLessonHandled,
  onSave,
  onDelete,
}: {
  lesson: AdminCourseLesson;
  indexLabel: number;
  videos: Video[];
  audios: Audio[];
  busy: boolean;
  focusLessonId?: string | null;
  onFocusLessonHandled?: () => void;
  onSave: (
    lessonId: string,
    p: { title: string; caption: string; published: boolean; videoId: string; audioId: string },
  ) => void;
  onDelete: (id: string) => void;
}) {
  const [title, setTitle] = useState(lesson.title);
  const [caption, setCaption] = useState(lesson.caption || '');
  const [published, setPublished] = useState(lesson.published);
  const [videoId, setVideoId] = useState(lesson.videoId || '');
  const [audioId, setAudioId] = useState(lesson.audioId || '');
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTitle(lesson.title);
    setCaption(lesson.caption || '');
    setPublished(lesson.published);
    setVideoId(lesson.videoId || '');
    setAudioId(lesson.audioId || '');
  }, [lesson]);

  useEffect(() => {
    if (focusLessonId !== lesson.id || !titleInputRef.current) return;
    titleInputRef.current.focus();
    titleInputRef.current.select();
    onFocusLessonHandled?.();
  }, [focusLessonId, lesson.id, onFocusLessonHandled]);

  const hasVideo = !!videoId;
  const hasAudio = !!audioId;

  return (
    <div id={`lesson-editor-${lesson.id}`} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">
            جلسه {indexLabel}
          </span>
          {hasVideo ? (
            <span className="rounded-md bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800">ویدیو</span>
          ) : null}
          {hasAudio ? (
            <span className="rounded-md bg-purple-100 px-2 py-0.5 text-[11px] font-medium text-purple-800">صوت</span>
          ) : null}
          <span className="text-xs text-slate-500">مدت: {formatDurationSec(lessonDurationSeconds(lesson))}</span>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={published}
            onChange={(e) => setPublished(e.target.checked)}
            className="rounded border-slate-300"
          />
          منتشر در سایت
        </label>
      </div>

      <div className="grid gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">عنوان جلسه</label>
          <input
            ref={titleInputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">کپشن / توضیح کوتاه</label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">ویدیو جلسه</label>
            {videos.length > 0 && !hasVideo ? (
              <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] font-semibold leading-relaxed text-amber-950">
                ویدیو را انتخاب کنید و «ذخیره جلسه» را بزنید.
              </p>
            ) : null}
            <select
              value={videoId}
              onChange={(e) => setVideoId(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">— بدون ویدیو —</option>
              {videos.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.title || v.id} ({formatDurationSec(v.duration)})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">فایل صوتی جلسه</label>
            {audios.length > 0 && !hasAudio ? (
              <p className="mb-2 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-2 text-[11px] font-semibold leading-relaxed text-violet-950">
                صوت را انتخاب کنید و «ذخیره جلسه» را بزنید.
              </p>
            ) : null}
            <select
              value={audioId}
              onChange={(e) => setAudioId(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">— بدون صوت —</option>
              {audios.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title || a.id} ({formatDurationSec(a.duration)})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            onSave(lesson.id, {
              title,
              caption,
              published,
              videoId,
              audioId,
            })
          }
          className="rounded-lg bg-[#1C3B9B] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#15307d] disabled:opacity-50"
        >
          ذخیره جلسه
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onDelete(lesson.id)}
          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          حذف جلسه
        </button>
      </div>
    </div>
  );
}
